<template lang="html">
  <div :class="$style.block">
    <div v-for="(group, groupId) in clientsByGroup" :key="`${groupId}-${count.groups}`" :class="$style.group">
      <div v-for="(client, clientId) in group" :key="clientId" :class="$style.item">
        <img :src="client.url" :alt="client.name" :class="$style.logo" />
      </div>
    </div>
  </div>
</template>

<script>
import chunk from 'lodash/chunk';
import shuffle from 'lodash/shuffle';

import { BREAKPOINTS } from '~scripts/core/breakpoints';

const context = window.clientsLogotypesType ? window.clientsLogotypesType : window.region;
const clientsContext = {
  ru: require.context('images/blocks/clients/ru', false, /.svg/),
  kz: require.context('images/blocks/clients/kz', false, /.svg/),
  uz: require.context('images/blocks/clients/uz', false, /.svg/),
  ai: require.context('images/blocks/clients/ai', false, /.svg/),
  enterprise: require.context('images/blocks/clients/enterprise', false, /.svg/),
}[context];

const logosShuffled = shuffle(
  clientsContext?.keys().map((k) => ({ name: k.slice(2, k.length - 4), url: clientsContext(k) }))
);

export default {
  name: 'VClientsLogotypes',
  data() {
    const mediaQuery = {
      md: window.matchMedia(`(min-width: ${BREAKPOINTS.mdMin})`),
      xl: window.matchMedia(`(min-width: ${BREAKPOINTS.xlMin})`),
    };
    return {
      mediaQuery: mediaQuery,
      count: this.getCount(mediaQuery),
    };
  },
  computed: {
    clientsByGroup() {
      return chunk(logosShuffled, this.count.clientsInGroup).slice(0, this.count.groups);
    },
  },
  created() {
    this.mediaQuery.md.addEventListener('change', this.handleMediaMatch);
    this.mediaQuery.xl.addEventListener('change', this.handleMediaMatch);
  },
  methods: {
    getCount(mediaQuery) {
      return mediaQuery.xl.matches
        ? { groups: 5, clientsInGroup: 3 }
        : mediaQuery.md.matches
          ? { groups: 4, clientsInGroup: 4 }
          : { groups: 3, clientsInGroup: 5 };
    },
    handleMediaMatch() {
      this.count = this.getCount(this.mediaQuery);
    },
  },
};
</script>

<style module lang="scss">
@use '~styles/core/_mixins.scss' as mixins;

@keyframes fadeIn3 {
  0% {
    opacity: 0;
  }

  7% {
    opacity: 1;
  }

  14% {
    opacity: 1;
  }

  21% {
    opacity: 0;
  }

  100% {
    opacity: 0;
  }
}

@keyframes fadeIn4 {
  0% {
    opacity: 0;
  }

  9.5% {
    opacity: 1;
  }

  19% {
    opacity: 1;
  }

  28.5% {
    opacity: 0;
  }

  100% {
    opacity: 0;
  }
}

@keyframes fadeIn5 {
  0% {
    opacity: 0;
  }

  12.5% {
    opacity: 1;
  }

  25% {
    opacity: 1;
  }

  37.5% {
    opacity: 0;
  }

  100% {
    opacity: 0;
  }
}

@mixin fadeIn($duration, $delay, $name) {
  animation: {
    delay: $delay;
    duration: $duration;
    fill-mode: both;
    iteration-count: infinite;
    name: $name; /* stylelint-disable-line value-keyword-case */
  }

  transition-timing-function: ease-in;
  will-change: opacity;
}

.block {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;

  @include mixins.media(md) {
    flex-wrap: nowrap;
  }

  @for $group from 1 through 3 {
    @for $client from 1 through 5 {
      .group:nth-child(#{$group}) .item:nth-child(#{$client}) {
        @include fadeIn(25s, calc(5s * (#{$client} - 1) + 0.3s * (#{$group} - 1)), fadeIn3);
      }
    }
  }

  @include mixins.media(md) {
    @for $group from 1 through 4 {
      @for $client from 1 through 4 {
        .group:nth-child(#{$group}) .item:nth-child(#{$client}) {
          @include fadeIn(24s, calc(6s * (#{$client} - 1) + 0.3s * (#{$group} - 1)), fadeIn4);
        }
      }
    }
  }

  @include mixins.media(xl) {
    @for $group from 1 through 5 {
      @for $client from 1 through 3 {
        .group:nth-child(#{$group}) .item:nth-child(#{$client}) {
          @include fadeIn(21s, calc(7s * (#{$client} - 1) + 0.3s * (#{$group} - 1)), fadeIn5);
        }
      }
    }
  }
}

.group {
  position: relative;
  display: inline-flex;

  @include mixins.media(md) {
    flex: 1 0 0;
  }
}

.item {
  display: inline-flex;
  width: 100%;
  pointer-events: none;

  &:not(:first-child) {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
}

.logo {
  @include mixins.size(100px, 51px);

  filter: opacity(0.5);

  @include mixins.media(md) {
    @include mixins.size(100%, auto);
  }
}
</style>
